/**
 * Admin API client — mirrors api.js but uses the admin JWT (labv_admin_token).
 * Never shares state or tokens with the regular chat UI.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const ADMIN_TOKEN_KEY = 'labv_admin_token';

function parseJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY) || '';
}

export function storeAdminToken(token) {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
}

export function clearAdminToken() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
}

export function isAdminTokenValid() {
  const token = getAdminToken();
  if (!token) return false;
  const payload = parseJwtPayload(token);
  if (!payload || !payload.exp) return false;
  return payload.exp > Math.floor(Date.now() / 1000);
}

function adminAuthHeaders(contentTypeJson = false) {
  const h = {};
  if (contentTypeJson) h['Content-Type'] = 'application/json';
  const token = getAdminToken();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

async function handleResponse(res) {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

function toQueryString(params) {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
  ).toString();
  return qs ? '?' + qs : '';
}

export async function adminLogin(username, password) {
  const res = await fetch(`${API_BASE_URL}/api/admin/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await handleResponse(res);
  storeAdminToken(data.token);
  return data.token;
}

export async function getAdminOverview(params = {}) {
  const res = await fetch(`${API_BASE_URL}/api/admin/overview${toQueryString(params)}`, {
    headers: adminAuthHeaders(),
  });
  return handleResponse(res);
}

export async function getAdminCompanies(params = {}) {
  const res = await fetch(`${API_BASE_URL}/api/admin/companies${toQueryString(params)}`, {
    headers: adminAuthHeaders(),
  });
  return handleResponse(res);
}

export async function getAdminCompanyNames() {
  const res = await fetch(`${API_BASE_URL}/api/admin/company-names`, {
    headers: adminAuthHeaders(),
  });
  return handleResponse(res);
}

export async function getAdminUserNames() {
  const res = await fetch(`${API_BASE_URL}/api/admin/user-names`, {
    headers: adminAuthHeaders(),
  });
  return handleResponse(res);
}

export async function getAdminDailySeries(params = {}) {
  const res = await fetch(`${API_BASE_URL}/api/admin/daily-series${toQueryString(params)}`, {
    headers: adminAuthHeaders(),
  });
  return handleResponse(res);
}

export async function getAdminTokenUsage(params = {}) {
  const res = await fetch(`${API_BASE_URL}/api/admin/token-usage${toQueryString(params)}`, {
    headers: adminAuthHeaders(),
  });
  return handleResponse(res);
}

export async function getAdminFeedback(params = {}) {
  const res = await fetch(`${API_BASE_URL}/api/admin/feedback${toQueryString(params)}`, {
    headers: adminAuthHeaders(),
  });
  return handleResponse(res);
}

export async function getAdminConversations(params = {}) {
  const res = await fetch(`${API_BASE_URL}/api/admin/conversations${toQueryString(params)}`, {
    headers: adminAuthHeaders(),
  });
  return handleResponse(res);
}

export async function getAdminConversation(sessionId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/conversations/${encodeURIComponent(sessionId)}`,
    { headers: adminAuthHeaders() }
  );
  return handleResponse(res);
}

export async function deleteAdminConversation(sessionId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/conversations/${encodeURIComponent(sessionId)}`,
    { method: 'DELETE', headers: adminAuthHeaders() }
  );
  return handleResponse(res);
}

export async function getAdminTraces(params = {}) {
  const res = await fetch(`${API_BASE_URL}/api/admin/traces${toQueryString(params)}`, {
    headers: adminAuthHeaders(),
  });
  return handleResponse(res);
}

export async function getAdminTraceSpans(traceId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/traces/${encodeURIComponent(traceId)}/spans`,
    { headers: adminAuthHeaders() }
  );
  return handleResponse(res);
}
